import { scaledPosition } from "./preview_speed.es6";

export const SCENE_FPS = 30;

export const secToFrame = (sec) => Math.round(sec * SCENE_FPS);

// Scene clock for Visor and editor: Web Audio time while it runs, else
// performance.now(). A source switch re-anchors so the playhead never jumps
// (a suspended AudioContext freezes currentTime).
export class SceneClock {
  // `onRun(running)` is called synchronously whenever the clock starts or stops.
  constructor({ getAudioTime = () => null, now = () => performance.now(), onRun = null } = {}) {
    this._getAudioTime = getAudioTime;
    this._now = now;
    this._onRun = onRun;
    this._rate = 1;
    this._running = false;
    this._base = 0;
    this._src = null;
    this._anchor = 0;
    this._lastPos = 0;
    this._lastMs = 0;
  }

  get rate() {
    return this._rate;
  }

  isRunning() {
    return this._running;
  }

  source() {
    return this._running ? this._src : "frozen";
  }

  positionSec() {
    if (!this._running) return this._base;
    const ms = this._now();
    const audio = this._readAudio();
    const want = audio != null ? "audio" : "perf";
    if (want !== this._src) {
      this._base = this._lastPos + ((ms - this._lastMs) / 1000) * this._rate;
      this._anchorTo(want, audio, ms);
    }
    const elapsed = this._src === "audio" ? audio - this._anchor : (ms - this._anchor) / 1000;
    this._lastPos = scaledPosition(elapsed, this._rate, this._base);
    this._lastMs = ms;
    return this._lastPos;
  }

  // No-op while running.
  start(atSec) {
    if (this._running) return;
    if (typeof atSec === "number") this._base = Math.max(0, atSec);
    this._running = true;
    this._anchorTo(null, null, this._now());
    if (this._onRun) this._onRun(true);
  }

  pause() {
    if (!this._running) return;
    this._base = this.positionSec();
    this._running = false;
    if (this._onRun) this._onRun(false);
  }

  seek(sec) {
    this._base = Math.max(0, Number(sec) || 0);
    if (this._running) this._anchorTo(null, null, this._now());
  }

  reset() {
    const wasRunning = this._running;
    this._running = false;
    this._base = 0;
    if (wasRunning && this._onRun) this._onRun(false);
  }

  setRate(rate) {
    const r = Number(rate) > 0 ? Number(rate) : 1;
    if (r === this._rate) return;
    if (this._running) {
      this._base = this.positionSec();
      this._anchorTo(null, null, this._now());
    }
    this._rate = r;
  }

  _readAudio() {
    const t = this._getAudioTime();
    return typeof t === "number" && isFinite(t) ? t : null;
  }

  _anchorTo(src, audio, ms) {
    if (src == null) {
      audio = this._readAudio();
      src = audio != null ? "audio" : "perf";
    }
    this._src = src;
    this._anchor = src === "audio" ? audio : ms;
    this._lastPos = this._base;
    this._lastMs = ms;
  }
}

// Timeline media that starts and stops with the scene clock itself, in the
// same call, instead of on the React update that follows it. The Visor's
// clock reports here; a member is `(running, sec, sceneId) => void`.
const playbackMembers = new Set();
export const scenePlayback = {
  running: false,
  sceneId: null,
  register(member) {
    playbackMembers.add(member);
    return () => playbackMembers.delete(member);
  },
  // A Visor begins or ends: nothing runs, whatever an earlier one left here.
  // (A stopped clock reports nothing by itself.)
  reset() {
    this.set(false, 0, null);
  },
  set(running, sec, sceneId) {
    this.running = running;
    this.sceneId = sceneId;
    playbackMembers.forEach((member) => {
      try {
        member(running, sec, sceneId);
      } catch (_) {
        /* one member must not stop the clock or the others */
      }
    });
  },
};

// The last explicit move of the playhead in the current run of a scene.
// Elements created during or after it (a clip mounted mid-way) still see
// where it pointed. It ends with the run: a scene that (re)starts from zero
// clears it, a pause does not. Ids are never reused.
export const sceneSeek = {
  id: 0,
  sec: 0,
  sceneId: null,
  mark(sec, sceneId) {
    this.id++;
    this.sec = sec;
    this.sceneId = sceneId;
  },
  clear() {
    this.id++;
    this.sceneId = null;
  },
};

// Scene time published on every screen frame, for animations that update
// the DOM between the 30 fps React frame updates. `frame`: the committed frame,
// told only when it or the scene changes.
const busSubs = new Set();
const frameSubs = new Set();
export const sceneTimeBus = {
  sec: 0,
  sceneId: null,
  frame: 0,
  frameSceneId: null,
  publish(sec, sceneId) {
    this.sec = sec;
    this.sceneId = sceneId;
    busSubs.forEach((fn) => fn(sec, sceneId));
  },
  subscribe(fn) {
    busSubs.add(fn);
    return () => busSubs.delete(fn);
  },
  commitFrame(frame, sceneId) {
    if (frame === this.frame && sceneId === this.frameSceneId) return;
    this.frame = frame;
    this.frameSceneId = sceneId;
    frameSubs.forEach((fn) => fn());
  },
  // Arrow: handed to useSyncExternalStore as is.
  subscribeFrame: (fn) => {
    frameSubs.add(fn);
    return () => frameSubs.delete(fn);
  },
};
